import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Skeleton, Tooltip } from '@patternfly/react-core';
import { AlertVariant } from '@patternfly/react-core';
import { faTimes, faExpand, faArrowsRotate } from '@fortawesome/pro-regular-svg-icons';
import { faGear, faSparkles, faCompass } from '@fortawesome/pro-solid-svg-icons';
import InsightsActions from './InsightsActions';
import ChatInterface, { ChatMessages, Message } from './ChatInterface';
import { InsightsActions as InsightsActionsAPI } from '../../api/lambda/InsightsActions';
import { InsightsActionsResponse } from '../../api/types';
import { InsightsActionsRequest } from '../../api/types/insights-actions-request';
import { useApplication, useIsGranted } from '../user/ApplicationProvider';
import { Permission } from '../../enums/permission.enum';
import { ZoneSettingEnum } from '../../api/zone/ZoneSetting';
import { useToast } from '@zeroedin-tech/zi-common-ui/lib';
import { useMount } from 'react-use';
import ChartMetaDataModal from '../../helpers/helper-components/ChartMetaDataModal';
import { Chart } from '../../api/charts/Chart';
import { TChartMetaDetails } from '../../api/charts/ChartMetaDetails';
import DiscoverQuestionsModal from './DiscoverQuestionsModal';
import { Question, TQuestion } from '../../api/questions/Questions';
import { DiscoverChatData } from '../../helpers/discover-chat-store';
import { TInsightsChangeEvent } from '../../api/types/insights-change-event';
import InsightsChangeNotice from './InsightsChangeNotice';
import { useNoticeAnchors } from '../../hooks/NoticeAnchorHooks';
import { DataBuilderTypes } from '../../enums/data-builder-types';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** Keeps a long editing session from growing the prompt without bound. */
const MAX_CHANGE_EVENTS = 20;

type TChartMetadata = InsightsActionsRequest['metadata'];

/**
 * Watches the chart's request metadata rather than its data: metadata is the
 * only thing that states what the user is looking at, so a change to it is a
 * change to what any answer should be based on. Every differing category is
 * reported — a date and a filter moving together are two things the reader has
 * to be told about, not one.
 */
const describeChanges = (
	prev: TChartMetadata,
	next: TChartMetadata
): Array<Pick<TInsightsChangeEvent, 'kind' | 'summary' | 'detail'>> => {
	const range = (m: TChartMetadata) =>
		`${m.time_range?.start_date ?? ''} to ${m.time_range?.end_date ?? ''}`;
	const names = (items: Array<{ name: string }>) => items.map((i) => i.name).join(', ');
	const changes: Array<Pick<TInsightsChangeEvent, 'kind' | 'summary' | 'detail'>> = [];

	if (range(prev) !== range(next)) {
		changes.push({
			kind: 'date',
			summary: 'Updated time range',
			detail: `Time range: ${range(prev)} → ${range(next)}`,
		});
	}
	if (prev.filters_applied.join('; ') !== next.filters_applied.join('; ')) {
		changes.push({
			kind: 'filter',
			summary: 'Adjusted filters',
			detail: `Filters: ${prev.filters_applied.join('; ') || 'none'} → ${
				next.filters_applied.join('; ') || 'none'
			}`,
		});
	}
	if (names(prev.key_measures) !== names(next.key_measures)) {
		changes.push({
			kind: 'measure',
			summary: 'Changed measures',
			detail: `Measures: ${names(prev.key_measures) || 'none'} → ${
				names(next.key_measures) || 'none'
			}`,
		});
	}
	if (names(prev.dimensions) !== names(next.dimensions)) {
		changes.push({
			kind: 'dimension',
			summary: 'Changed dimensions',
			detail: `Dimensions: ${names(prev.dimensions) || 'none'} → ${
				names(next.dimensions) || 'none'
			}`,
		});
	}

	return changes;
};

const retryWithBackoff = async <T,>(
	fn: () => Promise<T>,
	maxRetries = 3,
	baseDelayMs = 1000
): Promise<T> => {
	let lastError: Error | null = null;
	for (let attempt = 0; attempt < maxRetries; attempt++) {
		try {
			return await fn();
		} catch (error) {
			lastError = error as Error;
			if (attempt < maxRetries - 1) {
				const delayMs = baseDelayMs * Math.pow(2, attempt);
				console.log(`Retry attempt ${attempt + 1}/${maxRetries} after ${delayMs}ms`);
				await delay(delayMs);
			}
		}
	}
	throw lastError;
};

interface InsightsChartSidebarProps {
	name?: string;
	previewData?: { csvData?: string[] };
	insightsActionsRequest?: InsightsActionsRequest;
	chartAiMetadata?: string;
	insightsOpen?: boolean;
	setInsightsOpen?: (open: boolean) => void;
	chartId?: string;
	discoverChatData?: DiscoverChatData;
}

const InsightsChartSidebar: React.FC<InsightsChartSidebarProps> = ({
	name,
	previewData,
	insightsActionsRequest,
	chartAiMetadata,
	insightsOpen,
	setInsightsOpen,
	chartId,
	discoverChatData,
}) => {
	const { addToast } = useToast();
	const isGranted = useIsGranted();
	const hasViewQuestionPerm = isGranted(Permission.ViewQuestion);
	const { zoneSettings, user } = useApplication();

	const permissionsSet = useMemo(() => new Set(user.permissions || []), [user.permissions]);
	const aiPermKeysPresent = (user.permissions || []).some(
		(p) => typeof p === 'string' && p.startsWith('canUseAI')
	);
	const hasAISummariesPerm = aiPermKeysPresent
		? permissionsSet.has(Permission.CanUseAISummaries)
		: true;
	const hasAIChatPerm = aiPermKeysPresent ? permissionsSet.has(Permission.CanUseAIChat) : true;

	// Insights-disabled zone check
	const [isInsightsDisabled, setIsInsightsDisabled] = useState(false);

	useMount(() => {
		const disableZoneSetting = zoneSettings.find(
			(x) => x.zone_setting_enum_id == ZoneSettingEnum.AI_disabled
		);
		if (disableZoneSetting && disableZoneSetting.enabled) {
			setIsInsightsDisabled(true);
		}
	});

	// Insights data state
	const [insightsData, setInsightsData] = useState<InsightsActionsResponse | undefined>();
	const [isInsightsLoading, setIsInsightsLoading] = useState(false);
	const [insightsError, setInsightsError] = useState(false);
	const [hasAutoGenerated, setHasAutoGenerated] = useState(false);
	// hasAutoGenerated is cleared by Refresh so the panel will generate again;
	// this one records that insights have existed at all, which is what decides
	// whether a data change is worth annotating. It clears only on close.
	const hasGeneratedRef = useRef(false);

	// Chat state
	const [chatMessages, setChatMessages] = useState<Message[]>([]);
	const [isChatThinking, setIsChatThinking] = useState(false);
	const [isInsightsModalOpen, setIsInsightsModalOpen] = useState(false);
	const [hasInteracted, setHasInteracted] = useState(false);
	const [refreshKey, setRefreshKey] = useState(0);
	const [changeEvents, setChangeEvents] = useState<TInsightsChangeEvent[]>([]);

	// Chart meta details
	const [chartMetaDetails, setChartMetaDetails] = useState(chartAiMetadata);
	const [isChartMetaDetailsModalOpen, setIsChartMetaDetailsModalOpen] = useState(false);

	// Discover questions
	const [isDiscoverQuestionsModalOpen, setIsDiscoverQuestionsModalOpen] = useState(false);
	const [_fetchedQuestions, setFetchedQuestions] = useState<TQuestion[]>([]);

	// Refs
	const sidebarContainerRef = useRef<HTMLDivElement | null>(null);
	const insightsThreadRef = useRef<HTMLDivElement | null>(null);
	const isNearBottomRef = useRef(true);

	// Dynamic sidebar height — fires on mount, no setTimeout needed
	useEffect(() => {
		const sidebarEl = sidebarContainerRef.current;
		if (!sidebarEl) return;

		const panelEl = sidebarEl.closest('.right-sidebar-panel') as HTMLElement;
		if (!panelEl) return;

		const scrollContainer = panelEl.closest('.pf-c-page__main') || document.documentElement;

		let rafId: number | null = null;

		const updateHeight = () => {
			const panelRect = panelEl.getBoundingClientRect();
			const footer = document.querySelector('.ribbon-footer');
			const footerTop = footer ? footer.getBoundingClientRect().top : window.innerHeight;

			const bottom = Math.min(window.innerHeight, footerTop);
			const newHeight = Math.floor(Math.max(bottom - panelRect.top, 200));

			const currentHeight = parseInt(panelEl.style.height || '0', 10);
			if (Math.abs(newHeight - currentHeight) > 1) {
				panelEl.style.height = `${newHeight}px`;
				panelEl.style.maxHeight = `${newHeight}px`;
			}
		};

		const onScrollOrResize = () => {
			if (rafId !== null) return;
			rafId = requestAnimationFrame(() => {
				updateHeight();
				rafId = null;
			});
		};

		scrollContainer.addEventListener('scroll', onScrollOrResize, { passive: true });
		window.addEventListener('resize', onScrollOrResize, { passive: true });

		const subheader = document.querySelector('.sub-nav');
		let observer: ResizeObserver | null = null;
		if (subheader) {
			observer = new ResizeObserver(onScrollOrResize);
			observer.observe(subheader);
		}

		updateHeight();

		return () => {
			scrollContainer.removeEventListener('scroll', onScrollOrResize);
			window.removeEventListener('resize', onScrollOrResize);
			observer?.disconnect();
			if (rafId !== null) cancelAnimationFrame(rafId);
			panelEl.style.height = '';
			panelEl.style.maxHeight = '';
		};
	}, []);

	// Track whether the user is scrolled near the bottom of the insights thread
	useEffect(() => {
		const container = insightsThreadRef.current;
		if (!container) return;

		const handleScroll = () => {
			const { scrollTop, scrollHeight, clientHeight } = container;
			isNearBottomRef.current = scrollHeight - scrollTop - clientHeight < 100;
		};

		container.addEventListener('scroll', handleScroll);
		return () => container.removeEventListener('scroll', handleScroll);
	}, []);

	// Auto-scroll when near bottom or when the user just sent a message
	const scrollInsightsThread = () => {
		if (insightsThreadRef.current) {
			insightsThreadRef.current.scrollTop = insightsThreadRef.current.scrollHeight;
		}
	};

	useEffect(() => {
		if (chatMessages.length > 0 || isChatThinking) {
			if (isNearBottomRef.current || isChatThinking) {
				requestAnimationFrame(() => {
					requestAnimationFrame(() => {
						scrollInsightsThread();
					});
				});
			}
		}
	}, [chatMessages, isChatThinking]);

	const prevMetadataRef = useRef(insightsActionsRequest?.metadata);
	const changeEventIdRef = useRef(0);
	useEffect(() => {
		const next = insightsActionsRequest?.metadata;
		const prev = prevMetadataRef.current;
		prevMetadataRef.current = next;

		// Nothing to compare against on the first report, and nothing worth
		// annotating until there are insights on screen for it to contrast with.
		if (!prev || !next || !hasGeneratedRef.current) return;

		const changes = describeChanges(prev, next);
		if (changes.length === 0) return;

		const at = Date.now();
		setChangeEvents((events) =>
			[
				...events,
				...changes.map((c) => ({ id: ++changeEventIdRef.current, at, ...c })),
			].slice(-MAX_CHANGE_EVENTS)
		);
	}, [insightsActionsRequest]);

	const { noticesAnchoredAt, resetAnchors } = useNoticeAnchors(changeEvents, chatMessages.length);

	// Build chat context
	const buildChatContext = (
		csvData: string[] | undefined,
		insightsActions: InsightsActionsResponse | undefined
	): string => {
		const parts: string[] = [];

		if (name) {
			// The sidebar is shared with the table builder, so label the entity from
			// what the request says it is instead of hardcoding "Chart".
			const entityLabel =
				insightsActionsRequest?.logging?.entity_type === DataBuilderTypes.table
					? 'Table'
					: 'Chart';
			parts.push(`${entityLabel}: ${name}`);
		}

		if (chartMetaDetails) {
			parts.push(`Chart Description: ${chartMetaDetails}`);
		}

		const req = insightsActionsRequest;
		if (req?.metadata) {
			const measures = req.metadata.key_measures.map((m) => m.name).join(', ');
			const dims = req.metadata.dimensions.map((d) => d.name).join(', ');
			if (measures) parts.push(`Measures: ${measures}`);
			if (dims) parts.push(`Dimensions: ${dims}`);

			const { start_date, end_date } = req.metadata.time_range;
			if (start_date && end_date) {
				parts.push(`Time Range: ${start_date} to ${end_date}`);
			}

			if (req.metadata.filters_applied.length > 0) {
				parts.push(`Filters: ${req.metadata.filters_applied.join('; ')}`);
			}
		}

		if (csvData && csvData.length > 0) {
			const headers = csvData[0] || '';
			const sampleRows = csvData.slice(1, 6).join('\n');
			parts.push(`Dataset Headers: ${headers}`);
			parts.push(`Sample Data:\n${sampleRows}`);
		}

		if (insightsActions?.insights?.length) {
			parts.push(`Generated Insights:\n- ${insightsActions.insights.join('\n- ')}`);
		}

		if (insightsActions?.actions?.length) {
			parts.push(`Recommended Actions:\n- ${insightsActions.actions.join('\n- ')}`);
		}

		// The insights above describe the chart as it was when they were generated.
		if (changeEvents.length > 0) {
			const lines = changeEvents.map((ev) => `- ${ev.summary}: ${ev.detail}`).join('\n');
			parts.push(
				`Changes since these insights were generated (the data above may be out of date):\n${lines}`
			);
		}

		return parts.join('\n\n');
	};

	const chatContext = useMemo(
		() => buildChatContext(previewData?.csvData, insightsData),
		[
			previewData?.csvData,
			insightsData,
			name,
			chartMetaDetails,
			insightsActionsRequest,
			changeEvents,
		]
	);

	// Auto-generate insights
	const autoGenerateInsights = () => {
		if (insightsActionsRequest) {
			setIsInsightsLoading(true);
			setInsightsError(false);

			retryWithBackoff(() => InsightsActionsAPI.GetInsights(insightsActionsRequest), 3, 1000)
				.then((insights) => {
					if (insights.success) {
						if (typeof insights.data === 'string') {
							insights.data = JSON.parse(insights.data) as InsightsActionsResponse;
						}
						setInsightsData(insights.data);
						hasGeneratedRef.current = true;
					} else {
						setInsightsError(true);
						addToast(insights.message, AlertVariant.danger);
					}
				})
				.catch((): void => {
					setInsightsError(true);
					addToast('Error fetching insights after retries.', AlertVariant.danger);
				})
				.finally(() => {
					setIsInsightsLoading(false);
				});
		}
	};

	// Auto-generate on mount (when sidebar opens)
	useEffect(() => {
		// If coming from Discover page with pre-loaded insights, use those directly
		if (discoverChatData && !hasAutoGenerated) {
			setInsightsData(discoverChatData.insights);
			setHasAutoGenerated(true);
			hasGeneratedRef.current = true;
			return;
		}

		if (
			!hasAutoGenerated &&
			previewData?.csvData &&
			insightsActionsRequest &&
			!isInsightsDisabled &&
			hasAISummariesPerm
		) {
			setHasAutoGenerated(true);
			autoGenerateInsights();
		}
	}, []);

	// Fetch discover questions on mount
	const getDiscoverQuestions = () => {
		if (chartId) {
			Question.GetAll(parseInt(chartId))
				.then((questions) => {
					setFetchedQuestions(questions);
				})
				.catch(() => {
					addToast(
						'An error occured while fetching Discover Questions',
						AlertVariant.danger
					);
				});
		}
	};

	useEffect(() => {
		getDiscoverQuestions();
	}, []);

	// Reset state when sidebar closes
	useEffect(() => {
		if (!insightsOpen) {
			setHasAutoGenerated(false);
			setInsightsData(undefined);
			setInsightsError(false);
			setChatMessages([]);
			setIsChatThinking(false);
			setChangeEvents([]);
			resetAnchors();
			hasGeneratedRef.current = false;
		}
	}, [insightsOpen]);

	// Chart meta details handlers
	const closeChartMetadetailsModal = () => {
		setIsChartMetaDetailsModalOpen(false);
	};

	const saveChartMetaDetails = () => {
		const request: TChartMetaDetails = {
			chartId: chartId ? parseInt(chartId) : 0,
			metadetails: chartMetaDetails ?? '',
		};

		Chart.SaveChartMetaDetails(request)
			.then(() => {
				addToast('Chart metadata details saved successfully.', AlertVariant.success);
				setIsChartMetaDetailsModalOpen(false);
			})
			.catch(() => {
				addToast(
					'An error occurred while trying to save chart metadetails. Please try again later.',
					AlertVariant.danger
				);
			});
	};

	const handleRefresh = () => {
		setInsightsData(undefined);
		setHasAutoGenerated(false);
		setChatMessages([]);
		setIsChatThinking(false);
		setInsightsError(false);
		setHasInteracted(false);
		// Cleared up front, not on success: Refresh reposts the current chart, so
		// the drift these notices flagged is about to be gone either way.
		setChangeEvents([]);
		resetAnchors();
		setRefreshKey((k) => k + 1);
		autoGenerateInsights();
	};

	return (
		<div
			className="insights-sidebar-redesign"
			ref={sidebarContainerRef}
			role="complementary"
			aria-label="AI Insights"
		>
			{/* Sticky header */}
			<div className="insights-sticky-header">
				<div className="insights-sticky-header__left">
					<FontAwesomeIcon
						icon={faSparkles}
						color="#0073c3"
					/>
					<span className="insights-sticky-header__title">AI * Insights</span>
				</div>
				<div className="insights-sticky-header__right">
					{hasAISummariesPerm && (
						<Tooltip content="Refresh Insights">
							<Button
								variant="plain"
								className="insights-header-btn"
								onClick={handleRefresh}
							>
								<FontAwesomeIcon icon={faArrowsRotate} />
							</Button>
						</Tooltip>
					)}
					{chartId && (
						<Tooltip content="Chart Metadata">
							<Button
								variant="plain"
								className="insights-header-btn"
								onClick={() => setIsChartMetaDetailsModalOpen(true)}
							>
								<FontAwesomeIcon icon={faGear} />
							</Button>
						</Tooltip>
					)}
					{chartId && hasViewQuestionPerm && (
						<Tooltip content="Discover Questions">
							<Button
								variant="plain"
								className="insights-header-btn"
								onClick={() => setIsDiscoverQuestionsModalOpen(true)}
							>
								<FontAwesomeIcon icon={faCompass} />
							</Button>
						</Tooltip>
					)}
					<Tooltip content="Expand">
						<Button
							variant="plain"
							className="insights-header-btn"
							onClick={() => setIsInsightsModalOpen(true)}
						>
							<FontAwesomeIcon icon={faExpand} />
						</Button>
					</Tooltip>
					<Tooltip content="Close">
						<Button
							variant="plain"
							className="insights-header-btn"
							onClick={() => {
								setInsightsOpen && setInsightsOpen(false);
							}}
						>
							<FontAwesomeIcon icon={faTimes} />
						</Button>
					</Tooltip>
				</div>
			</div>

			{/* Scrollable thread */}
			<div
				className="insights-thread"
				ref={insightsThreadRef}
				tabIndex={0}
			>
				{!isInsightsDisabled && hasAIChatPerm && (
					<>
						{/* Loading skeletons */}
						{isInsightsLoading && !insightsData && (
							<div className="insights-loading">
								<Skeleton />
								<span className="spacer-xxs"></span>
								<Skeleton />
								<span className="spacer-xxs"></span>
								<Skeleton />
								<span className="spacer-xxs"></span>
								<Skeleton width="60%" />
							</div>
						)}

						{/* Error state with retry */}
						{insightsError && !insightsData && !isInsightsLoading && (
							<div style={{ padding: '1rem', textAlign: 'center' }}>
								<p style={{ color: '#6a6e73', marginBottom: '0.5rem' }}>
									Failed to generate insights.
								</p>
								<Button
									variant="link"
									onClick={() => {
										setInsightsError(false);
										autoGenerateInsights();
									}}
								>
									Try again
								</Button>
							</div>
						)}

						{/* Insights & Actions Card */}
						{insightsData && (
							<InsightsActions
								insights={insightsData}
								hasBackground={false}
								collapsible={false}
							/>
						)}

						{/* Chat messages in the shared thread, with muted change notices
						    interleaved where the chart's data changed */}
						{(chatMessages.length > 0 || changeEvents.length > 0 || isChatThinking) && (
							<div className="insights-chat-thread">
								{chatMessages.map((msg, idx) => (
									<React.Fragment key={`msg-${msg.id}`}>
										{noticesAnchoredAt(idx).map((ev) => (
											<InsightsChangeNotice
												key={`chg-${ev.id}`}
												event={ev}
												subject="chart"
											/>
										))}
										<ChatMessages
											messages={[msg]}
											isThinking={false}
										/>
									</React.Fragment>
								))}
								{noticesAnchoredAt(chatMessages.length).map((ev) => (
									<InsightsChangeNotice
										key={`chg-${ev.id}`}
										event={ev}
										subject="chart"
									/>
								))}
								{isChatThinking && (
									<ChatMessages
										messages={[]}
										isThinking={true}
									/>
								)}
							</div>
						)}
					</>
				)}

				{isInsightsDisabled && (
					<div className="insights-disabled-msg">
						AI Insights are disabled for this zone.
					</div>
				)}
			</div>

			{/* Pinned chat input at bottom */}
			{!isInsightsDisabled && hasAIChatPerm && (
				<div className="insights-pinned-input">
					{chatMessages.length === 0 &&
						!isChatThinking &&
						!hasInteracted &&
						insightsData && (
							<small className="insights-chat-prompt">Ask a follow-up question</small>
						)}
					{/* Keyed on refreshKey so Refresh remounts the chat: ChatInterface owns
					    the message list that is actually sent to the model, and clearing
					    our chatMessages mirror does not reach it. The remount also resets
					    its already-auto-submitted flag, hence the first-mount guard on
					    autoSubmitMessage below. */}
					<ChatInterface
						key={refreshKey}
						context={chatContext}
						externalThread={true}
						onMessagesChange={setChatMessages}
						onThinkingChange={setIsChatThinking}
						showModal={isInsightsModalOpen}
						onModalClose={() => setIsInsightsModalOpen(false)}
						onFirstInteraction={() => setHasInteracted(true)}
						modalHeaderContent={
							insightsData ? (
								<InsightsActions
									insights={insightsData}
									hasBackground={false}
									collapsible={false}
								/>
							) : undefined
						}
						disabled={!insightsData && !insightsError}
						autoSubmitMessage={
							refreshKey === 0 ? discoverChatData?.questionText : undefined
						}
					/>
				</div>
			)}

			{/* Chart Metadata Modal */}
			<ChartMetaDataModal
				isOpen={isChartMetaDetailsModalOpen}
				onClose={closeChartMetadetailsModal}
				onSubmit={() => {
					saveChartMetaDetails();
				}}
				onTextChange={(value) => {
					setChartMetaDetails(value);
				}}
				titleText={`Meta-details for Chart`}
				textValue={chartMetaDetails}
				textLabel={
					<>
						<p>
							Here you can configure additional context messages to be passed along
							for when AI insights are generated for the <b>{name}</b> chart
						</p>
					</>
				}
			/>

			{/* Discover Questions Modal */}
			<DiscoverQuestionsModal
				isOpen={isDiscoverQuestionsModalOpen}
				onClose={() => setIsDiscoverQuestionsModalOpen(false)}
				chartId={chartId ? parseInt(chartId) : undefined}
			/>
		</div>
	);
};

export default InsightsChartSidebar;
